// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'

export type Layer = 'accessory' | 'main' | 'navigation' | 'panel' | 'popup'
export type Level = 'section' | 'subsection'
export type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

/**
 * @slot Content to display for the title.
 * @see https://quartz.se.com/build/components/title
 */
@Component({
  tag: 'qds-title',
  shadow: true,
  styleUrl: 'title.css',
})
export class Title implements ComponentInterface {
  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * Text to display for the kicker.
   */
  @Prop() public readonly kicker?: string

  @Prop() public readonly layer: Layer = 'main'

  @Prop() public readonly level?: Level = 'section'

  /**
   * Text to display for the subtitle.
   */
  @Prop() public readonly subtitle?: string

  /**
   * HTML tag to use to render the title.
   */
  @Prop() public readonly tag: Tag = 'h2'

  @Element() private readonly element!: HTMLQdsTitleElement

  private inheritedAttributes: Attributes = {}

  private get hasSubtitle(): boolean {
    return this.subtitle !== undefined && this.subtitle !== ''
  }

  private get hasKicker(): boolean {
    return this.kicker !== undefined && this.kicker !== ''
  }

  public componentWillLoad(): void {
    this.inheritedAttributes = inheritAriaAttributes(this.element)
  }

  public render() {
    const layer = `qds-${this.layer}`
    const level = `qds-${this.level ?? 'root'}`

    return (
      <hgroup
        class={{
          [layer]: true,
          [level]: true,
          'qds-titles': true,
          'qds-has-icon': this.iconName !== undefined && this.iconName !== '',
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.inheritedAttributes}
      >
        <this.tag class="qds-title">
          <slot />
        </this.tag>
        {this.hasSubtitle && <p class="qds-subtitle">{this.subtitle}</p>}
        {this.hasKicker && <p class="qds-kicker">{this.kicker}</p>}
        {this.iconName !== undefined && this.iconName !== '' && (
          <qds-icon
            class="qds-icon"
            name={this.iconName}
            library={this.iconLibrary}
          />
        )}
      </hgroup>
    )
  }
}
